import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import updateSourceDataById from "@app/services/cases/updateSourceDataById";

import { useToast } from "@hooks/useToast";

import { CASES_DATA_STATUS_TYPE } from "@utils/enum";

import { useDataSourceDetailsContext } from "../../providers";
import {
  getParsedCrimeData,
  prepareCrimeData,
} from "../CrimesTab/CrimesTab.utils";
import { getOtherCasesDataTableConfig } from "./OtherCasesTab.utils";

export const useOtherCasesTab = () => {
  const { dataSourceDetails } = useDataSourceDetailsContext();
  const {
    otherCaseToDelete,
    handleCloseDeleteOtherCaseDialog,
    handleOpenDeleteOtherCaseDialog,
  } = useOtherCasesTabSelectedCrimes();
  const { handleDeleteOtherCase } = useOtherCasesTabActions();

  const sourceDataType = dataSourceDetails?.type ?? "";

  const crimesMap = useMemo(() => {
    const rawData = dataSourceDetails?.data;

    if (!rawData) {
      return {
        listOfCrimes: [],
        dismissedCrimes: [],
        indictments: [],
        otherCases: [],
      };
    }

    return getParsedCrimeData(rawData);
  }, [dataSourceDetails?.data]);

  const isCrimesEditable = useMemo(() => {
    return dataSourceDetails?.status !== CASES_DATA_STATUS_TYPE.COMPLETED;
  }, [dataSourceDetails?.status]);

  const otherCasesConfig = useMemo(
    () =>
      getOtherCasesDataTableConfig(crimesMap.otherCases, isCrimesEditable, {
        onOpenDeleteOtherCaseDialog: handleOpenDeleteOtherCaseDialog,
      }),
    [crimesMap.otherCases, handleOpenDeleteOtherCaseDialog, isCrimesEditable]
  );

  const handleConfirmDeleteOtherCase = useCallback(() => {
    handleDeleteOtherCase(otherCaseToDelete);
    handleCloseDeleteOtherCaseDialog();
  }, [
    handleDeleteOtherCase,
    handleCloseDeleteOtherCaseDialog,
    otherCaseToDelete,
  ]);

  return {
    otherCasesConfig,
    sourceDataType,
    otherCaseToDelete,
    handleConfirmDeleteOtherCase,
    handleCloseDeleteOtherCaseDialog,
  };
};

export const useOtherCasesTabActions = () => {
  const { dataSourceDetails, setDataSourceDetails } =
    useDataSourceDetailsContext();
  const { messages } = useIntl();
  const { case_id, data_source_id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();

  const processedData = useMemo(
    () => dataSourceDetails?.data?.processed_data ?? [],
    [dataSourceDetails?.data?.processed_data]
  );

  const handleUpdateSourceData = useCallback(
    async nextValues => {
      const payload = {
        processed_data: nextValues,
      };

      try {
        await updateSourceDataById(case_id, data_source_id, payload);

        setDataSourceDetails(prev => ({
          ...prev,
          data: {
            ...prev.data,
            processed_data: nextValues,
          },
        }));

        showSuccessToast(messages.text_update_successful);
      } catch {
        showErrorToast(messages.exception_error_message);
      }
    },
    [
      case_id,
      data_source_id,
      messages.exception_error_message,
      messages.text_update_successful,
      setDataSourceDetails,
      showErrorToast,
      showSuccessToast,
    ]
  );

  const handleDeleteOtherCase = useCallback(
    async ({ document_id }) => {
      const filteredCrimesList = processedData.filter(
        crime => crime.document_id !== document_id
      );

      await handleUpdateSourceData(filteredCrimesList);
    },
    [handleUpdateSourceData, processedData]
  );

  const handleEditOtherCase = useCallback(
    async otherCaseToEdit => {
      const formattedOtherCaseToEdit = prepareCrimeData(otherCaseToEdit);
      const updatedCrimesList = processedData.map(crime =>
        crime.document_id === formattedOtherCaseToEdit.document_id
          ? formattedOtherCaseToEdit
          : crime
      );

      await handleUpdateSourceData(updatedCrimesList);
    },
    [handleUpdateSourceData, processedData]
  );

  return {
    handleDeleteOtherCase,
    handleEditOtherCase,
  };
};

export const useOtherCasesTabSelectedCrimes = () => {
  const [otherCaseToDelete, setOtherCaseToDelete] = useState(null);

  const handleCloseDeleteOtherCaseDialog = useCallback(() => {
    setOtherCaseToDelete(null);
  }, []);

  const handleOpenDeleteOtherCaseDialog = useCallback(otherCase => {
    setOtherCaseToDelete(otherCase);
  }, []);

  return {
    otherCaseToDelete,
    handleCloseDeleteOtherCaseDialog,
    handleOpenDeleteOtherCaseDialog,
  };
};
